import { HeartHandshake, Scale, Check, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/site/PageHeader";
import { BannerStrip } from "@/components/site/BannerStrip";
import { Services } from "@/components/site/Services";
import { Reveal } from "@/components/site/Reveal";
import { PoweredBy } from "@/components/site/PoweredBy";
import { useAgentSelect } from "@/components/site/AgentSelect";
import { LIFE_OPTIONS, LEGALSHIELD_SERVICES, CONTACT, KMD, KMD_SERVICES } from "@/data";

export default function ServicesPage() {
  const { openAgentSelect } = useAgentSelect();
  return (
    <>
      <PageHeader
        overline="Our Protection"
        title="Everything a family needs,"
        accent="under one shield."
        subtitle="From life and health to mortgage, legal, retirement, and tax — explore the full range of solutions Heritage helps families access."
      />

      <BannerStrip testId="services-banner" />

      <Services />

      {/* Life Insurance detail */}
      <section id="life-detail" className="relative py-20 md:py-28 bg-[#0a192f] border-y border-[#bf953f]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-[#bf953f]/30 text-[#d4af37]">
              <HeartHandshake size={22} strokeWidth={1.5} />
            </span>
            <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white">
              Life Insurance <span className="text-gold-gradient italic">options</span>
            </h2>
            <p className="mt-5 max-w-2xl font-body font-light text-base md:text-lg leading-relaxed text-white/60">
              We help clients explore a full spectrum of life insurance designed to replace income, cover final
              expenses, and leave a tax-advantaged legacy for the people who matter most.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
            {LIFE_OPTIONS.map((o, i) => (
              <Reveal key={o.name} delay={(i % 2) * 0.06}>
                <div
                  data-testid={`life-option-${i}`}
                  className="group h-full rounded-2xl border border-[#bf953f]/20 bg-[#081226] p-7 hover:border-[#d4af37]/50 transition-colors duration-500"
                >
                  <div className="flex items-start gap-3">
                    <Check size={18} className="mt-1 shrink-0 text-[#d4af37]" />
                    <div>
                      <h3 className="font-serif-display text-2xl text-white">{o.name}</h3>
                      <p className="mt-2 font-body font-light text-white/55 leading-relaxed">{o.desc}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LegalShield detail */}
      <section id="legal-detail" className="relative py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex flex-wrap items-center gap-5">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-[#bf953f]/30 text-[#d4af37]">
                <Scale size={22} strokeWidth={1.5} />
              </span>
              <PoweredBy />
            </div>
            <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white">
              LegalShield — <span className="text-gold-gradient italic">legal protection for every family.</span>
            </h2>
            <p className="mt-5 max-w-3xl font-body font-light text-base md:text-lg leading-relaxed text-white/60">
              Heritage offers affordable legal service memberships powered by LegalShield. Depending on the plan and
              location, these memberships may provide access to attorneys for everyday personal, family, and business
              matters — plus identity-theft protection and more.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
            {LEGALSHIELD_SERVICES.map((s, i) => (
              <Reveal key={s} delay={(i % 3) * 0.05}>
                <div className="flex items-start gap-3 border-t border-white/10 pt-4">
                  <Check size={17} className="mt-0.5 shrink-0 text-[#d4af37]" />
                  <span className="font-body text-white/70 leading-relaxed">{s}</span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-14">
              <a
                href={CONTACT.phoneHref}
                data-testid="legal-cta"
                className="btn-sweep inline-flex items-center rounded-full bg-gold-metallic px-8 py-3.5 font-body text-sm font-medium tracking-wide text-black"
              >
                Ask About a LegalShield Membership
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* KonnectMD detail */}
      <section id="konnectmd-detail" className="relative py-20 md:py-28 border-t border-white/10" style={{ background: "linear-gradient(180deg, rgba(17,115,185,0.1) 0%, #081226 70%)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="inline-flex items-center rounded-xl bg-white px-5 py-3">
              <img src={KMD.logo} alt="KonnectMD" data-testid="services-kmd-logo" className="h-9 md:h-10 w-auto object-contain" />
            </div>
            <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white">
              Virtual healthcare, <span className="italic" style={{ color: "#6db6e8" }}>powered by KonnectMD.</span>
            </h2>
            <p className="mt-5 max-w-3xl font-body font-light text-base md:text-lg leading-relaxed text-white/60">
              Affordable, 24/7 national virtual healthcare with no visit fees, no copays, and over 1,000 common
              medications included free. Memberships start at $59.99/month and cover your partner and children
              under 26 — no insurance required.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
            {KMD_SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.05}>
                <div className="border-t border-white/10 pt-4" data-testid={`services-kmd-item-${i}`}>
                  <div className="flex items-start gap-3">
                    <Check size={17} className="mt-0.5 shrink-0" style={{ color: "#6db6e8" }} />
                    <div>
                      <h3 className="font-body font-medium text-white">{s.title}</h3>
                      <p className="mt-1 font-body font-light text-sm text-white/55 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-14 flex flex-wrap gap-4">
              <Link
                to="/konnectmd"
                data-testid="services-kmd-explore"
                className="btn-sweep inline-flex items-center gap-2 rounded-full px-8 py-3.5 font-body text-sm font-semibold tracking-wide text-white transition-transform duration-300 hover:scale-[1.03]"
                style={{ backgroundColor: KMD.blue }}
              >
                Explore KonnectMD Plans <ArrowUpRight size={15} />
              </Link>
              <button
                onClick={openAgentSelect}
                data-testid="services-kmd-enroll"
                className="inline-flex items-center gap-2 rounded-full border px-8 py-3.5 font-body text-sm tracking-wide text-white/80 hover:text-white transition-colors duration-300"
                style={{ borderColor: "rgba(17,115,185,0.5)" }}
              >
                Enroll Now <ArrowUpRight size={15} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
